import { useCallback } from "react"

import { SearchDetailResult } from "interface/search"
import { TargetListResult } from "interface/search/targetList"
import { KojinResult } from "interface/kojin"

import { Grid, useGrid } from "components/grid"

import { KojinPreviewDialog } from "features/kojin/dialog/preview"
import { useKojinPreviewDialog } from "features/kojin/dialog/preview/hooks"

import { useTargetGridColumns } from "./columns"
import { SourceProject } from "../../types"
import { KojinResultWithEvaluationSummary } from "interface/search/evaluation"
import { EvaluationFormResult } from "interface/evaluationForm"

type TargetPageProps = {
	search: SearchDetailResult
	sheetName: string | undefined
	saveKey: string
	loading: boolean
	progress: number | undefined
	kojinList: KojinResultWithEvaluationSummary[]
	evaluationForm: EvaluationFormResult | null,
	categories?: TargetListResult['category']
	scores?: TargetListResult['score']
	projectList: SourceProject[]
	onChangeEvaluation: (kojinCode: string) => void
} & ReturnType<typeof useGrid>
export const TargetPage = ({
	search,
	sheetName,
	saveKey,
	loading,
	progress,
	kojinList,
	evaluationForm,
	categories,
	scores,
	projectList,
	onChangeEvaluation,
	...others
}: TargetPageProps) => {
	const {
		kojinList: selectedKojinList,
		kojinCode: selectedKojinCode,
		authorId: selectedAuthorId,
		setKojinList,
		openKojin,
		close: closeKojinDialog
	} = useKojinPreviewDialog()

	const { apiRef } = others

	const onClickLink = useCallback(
		(kojinCode: string | null) => {
			if (!apiRef.current) return
			const api = apiRef.current

			const rowDataList: KojinResult[] = []
			const rowCount = api.getRowsCount()
			for(let i = 0; i < rowCount; i++) {
				const index = api.getRowIdFromRowIndex(i) as number
				if (index >= 0 && kojinList.length > index) {
					rowDataList.push(kojinList[index])
				}
			}
			setKojinList(rowDataList)
			openKojin(kojinCode)
		},
		[setKojinList, openKojin, apiRef, kojinList]
	)
	const onDblClick = useCallback(
		(kojin: KojinResult) => onClickLink(kojin.kojinCode ?? null),
		[onClickLink]
	)
	const { columnsDef } = useTargetGridColumns({
		search, evaluationForm, categories, scores, projectList,
		onClickLink
	})
	return (
		<>
			<Grid
				{...others}
				saveKey={saveKey}
				baseFileName={search.title} 
				sheetName={sheetName}
				loading={loading}
				progress={progress}
				columnsDef={columnsDef}
				dataList={kojinList}
				onDblClick={onDblClick}
			/>
			{(selectedKojinCode || selectedAuthorId) ? (
				<KojinPreviewDialog
					kojinList={selectedKojinList}
					kojinCode={selectedKojinCode ?? undefined}
					authorId={selectedAuthorId ?? undefined}
					onChangeEvaluation={onChangeEvaluation}
					onClose={closeKojinDialog}
				/>
			) : <></>}
		</>
	)
}